import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useDispatch, useSelector } from "react-redux";
import { setLanguage } from "../redux/slices/appslice";
import { setAppearance } from "../redux/slices/appearanceslice"; 
import { setProcessing, setUserTheme } from "../redux/slices/currentthemeslice";
import { apiErrorsHandler, getErrorMessages } from "../api/httpclient";
import { addNotification } from "../redux/slices/notificationsslice";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { notificationDurationMiddle } from "../lib/utils";

const options = [
  { value: "en", label: "En", flag: "https://flagcdn.com/24x18/gb.png" },
  { value: "fr", label: "Fr", flag: "https://flagcdn.com/24x18/fr.png" },
  { value: "ar", label: "عر", flag: "https://flagcdn.com/24x18/sa.png" },
];

export default function LanguageSelect({
  currentTheme,
  appearance,
  currentUser,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const app = useSelector((state) => state.app);
  let lang = app.language; 

  const itemRender = (li, itemProps) => {
    const item = itemProps.dataItem;
    const displayLabel = item.value === "ar" ? "Ar" : item.label;
    return (
      <li
        {...li.props}
        className="language-select-option flex items-center gap-2 px-2 py-1 cursor-pointer k-item"
        style={{
          direction: appearance.isRightToLeft ? "rtl" : "ltr",
        }}
      >
        <img
          src={item.flag}
          alt={displayLabel}
          style={{
            width: "15px",
            height: "auto",
            marginRight: appearance.isRightToLeft ? "0" : "5px",
            marginLeft: appearance.isRightToLeft ? "5px" : "0",
          }}
        />
        <label style={{ cursor: "pointer" }}>{displayLabel}</label>
      </li>
    );
  };

  const valueRender = (element, value) => {
    if (!value) return element;
    const displayLabel = value.value === "ar" ? "Ar" : value.label;
    return (
      <span className="flex items-center">
        <img
          src={value.flag}
          alt={displayLabel}
          style={{
            width: "15px",
            height: "auto",
            marginRight: appearance.isRightToLeft ? "0" : "5px",
            marginLeft: appearance.isRightToLeft ? "5px" : "0",
          }}
        />
        <span>{displayLabel}</span>
      </span>
    );
  };
  const { t } = useTranslation();

  return (
    <>
      {/* <SvgIcon icon={KendoIcons.globeIcon} className="globe" /> */}
      <DropDownList
        data={options}
        textField="label"
        value={options.find((e) => e.value == lang)}
        itemRender={itemRender}
        valueRender={valueRender}
        popupSettings={{
          className: "language-select-popup",
          width: 40,
        }}
        onChange={(e) => {
          const selected = e.value; 
          dispatch(setLanguage(selected.value));
          let isRightToLeft = selected.value === "ar";
          if (currentUser.current.IsAuthenticated) {
            let langId = 1;
            switch (selected.value) {
              case "en":
                langId = 1;
                break;
              case "ar":
                langId = 2;
                break;
              case "fr":
                langId = 3;
                break;
              default:
                langId = 1;
            }
            dispatch(
              setUserTheme(
                currentTheme.id,
                isRightToLeft,
                currentUser.current.SideBarType,
                langId,
                t
              )
            ).then(
              (response) => {
                dispatch(setAppearance({ isRightToLeft: isRightToLeft }));
              },
              (err) => {
                //hide loader
                dispatch(setProcessing({ processing: false }));

                const handleResponseReceived = (
                  state,
                  dispatch,
                  navigate,
                  payload
                ) => {
                  let errorMessages = payload?.data?.ErrorMessages;

                  if (!errorMessages || errorMessages.length == 0)
                    errorMessages = [t("currentuser.unknown_error")];
                  else errorMessages = getErrorMessages(errorMessages, 1);

                  dispatch(
                    addNotification({
                      text: errorMessages[0],
                      type: "error",
                      closable: true,
                      duration: notificationDurationMiddle,
                      position: "topRight",
                    })
                  );
                };

                const handleUnhandled = (
                  state,
                  dispatch,
                  navigate,
                  payload
                ) => {
                  let errorMessage = t("currentuser.unknown_error");
                  dispatch(
                    addNotification({
                      text: errorMessage,
                      type: "error",
                      closable: true,
                      duration: notificationDurationMiddle,
                      position: "topRight",
                    })
                  );
                };

                apiErrorsHandler({}, dispatch, navigate, err, null, {
                  responseReceived: handleResponseReceived,
                  unhandled: handleUnhandled,
                });
              }
            );
          } else {
            dispatch(setAppearance({ isRightToLeft: isRightToLeft }));
          }
        }}
        className="language-select"
      />

      <style>
        {`
        .app-header .globe{
        margin-top:-5px;
             margin-left:10px; 
            margin-right:10px;
            color: ${currentTheme.appBarLinkColor}
        }
          
        /* Main dropdown button */
        .app-header .language-select {
          width: fit-content;
          padding: 0px 6px;
          border: 0 solid #e48f27;
          border-radius: 6px;
          font-size: 12px;
          background-color: ${currentTheme.appBarHomeBg};
          color: ${currentTheme.appBarLinkColor};
          outline: 0;
          cursor: pointer;
          margin-top: -5px;
          direction: ${appearance.isRightToLeft ? "rtl" : "ltr"};
        }

        .app-header .language-select .k-button {
          padding: 0;
        }

        @media (max-width: 1024px) {
          /* Keep the trigger compact, while leaving the language code clickable. */
          .app-header .language-select img,
          .language-select-option img {
            display: none;
          }

          .app-header .language-select .k-input-button {
            display: none;
          }
        }

        /* 🟢 DROPDOWN POPUP CUSTOMIZATION */
        .k-list-container.k-popup {
          background-color: ${currentTheme.appBarHomeBg} ;
          border: 1px solid ${currentTheme.appBarHomeBg} ;
          color: ${currentTheme.appBarLinkColor} ;
          overflow: hidden;
        }

        .k-list .k-item {
          background: ${currentTheme.appBarHomeBg} ;
          color:  ${currentTheme.appBarLinkColor} ;
        }

        .k-list .k-item.k-focus,
        .k-list .k-item:hover {
          background-color: ${currentTheme.appBarHomeBg} ;
        }

        .k-list .k-item.k-selected {
          background-color: ${currentTheme.appBarHomeBg};
          color: ${currentTheme.appBarLinkColor} ;
        }
        `}
      </style>
    </>
  );
}
